import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { DismissRegular, Sparkle20Filled } from '@fluentui/react-icons';
import Confetti from './Confetti';
import { useTranslation } from '../../i18n';
import { MOMENT_VISIBLE_MS, MomentContext, usePrefersReducedMotion, type Moment } from './moments';

// Mounted ONCE around the signed-in app. From the fleet primitive, each rule enforced here:
//   - not a modal: a banner pinned to the top edge that never takes focus and never blocks input;
//   - announced politely: the live region is ALWAYS mounted, so a moment is read out when it lands;
//   - dismissable, and gone on its own after MOMENT_VISIBLE_MS — paused while hovered or focused;
//   - prefers-reduced-motion: words only, no confetti element at all.

const useStyles = makeStyles({
  region: {
    position: 'fixed',
    top: `calc(env(safe-area-inset-top, 0px) + ${tokens.spacingVerticalL})`,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    maxWidth: 'min(560px, calc(100vw - 32px))',
    pointerEvents: 'none',
  },
  banner: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow16,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke2}`,
    pointerEvents: 'auto',
  },
  icon: { color: tokens.colorBrandForeground1, flexShrink: 0 },
  text: { display: 'flex', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalXS },
  title: { fontWeight: tokens.fontWeightSemibold },
});

export default function MomentHost({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [current, setCurrent] = useState<Moment | null>(null);
  const [paused, setPaused] = useState(false);

  const show = useCallback((moment: Moment) => {
    setPaused(false);
    setCurrent(moment);
  }, []);
  const api = useMemo(() => ({ show }), [show]);

  useEffect(() => {
    if (!current || paused) return;
    const handle = window.setTimeout(() => setCurrent(null), MOMENT_VISIBLE_MS);
    return () => window.clearTimeout(handle);
  }, [current, paused]);

  return (
    <MomentContext.Provider value={api}>
      {children}
      <div className={styles.region} role="status" aria-live="polite" data-testid="moment-region">
        {current && (
          <div
            className={styles.banner}
            data-testid="moment-banner"
            data-kind={current.kind}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
          >
            {current.confetti && !reducedMotion && <Confetti />}
            <Sparkle20Filled className={styles.icon} aria-hidden="true" />
            <span className={styles.text}>
              <span className={styles.title}>{current.title}</span>
              {current.detail && <span>{current.detail}</span>}
            </span>
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              aria-label={t('shell.dismiss')}
              onClick={() => setCurrent(null)}
            />
          </div>
        )}
      </div>
    </MomentContext.Provider>
  );
}
